import React, { useState } from 'react'
import { Modal } from 'antd'
import wanganqiangQrCode from '../../../../images/service-qrcode/wanganqiang.png'
import './index.scss'
import {
  BankOutlined,
  ClockCircleOutlined,
  CustomerServiceOutlined,
  FileTextOutlined,
  StarOutlined,
  TagsOutlined
} from '@ant-design/icons'

interface EnterpriseAiUpgradeModalProps {
  visible: boolean
  onCancel: () => void
}

// 弹窗内图标字符串 → antd6 组件查表
const ENTERPRISE_ICONS: Record<string, React.ComponentType<any>> = {
  'file-text': FileTextOutlined,
  tags: TagsOutlined,
  'customer-service': CustomerServiceOutlined,
  bank: BankOutlined,
  star: StarOutlined,
  'clock-circle': ClockCircleOutlined
}
const BenefitIcon = ({ name }: { name: string }) => {
  const C = ENTERPRISE_ICONS[name]
  return C ? <C /> : null
}

const ENTERPRISE_AI_UPGRADE_PRIVILEGES = [
  {
    number: '特权一',
    title: 'AI 全链路数据互通特权',
    description:
      '专属加密 AI MCP 数据通道，无缝打通企业全场景业务数据，AI 一键调取、实时联动分析，彻底告别数据孤岛、手动导表、人工核对，管理效率直接翻倍。',
    tags: ['加密MCP通道', '全场景数据互通', '实时联动分析']
  },
  {
    number: '特权二',
    title: '千万级 AI Token 免费送',
    description:
      '开通/续费即赠 1000 万通用 AI Token，全场景无门槛使用，无需额外付费。百人团队全年高频 AI 调用需求全覆盖，直接省下数万元 AI 采购成本。',
    tags: ['1000万Token', '全场景通用', '省数万元成本']
  },
  {
    number: '特权三',
    title: '蓝领 AI Agent 平台使用权',
    description:
      '开通即获「今日水印相机 × 蓝领 Agent 平台」2 个行业 AI 管家使用权限。平台提供 20+ 行业模版、90+ MCP 能力，覆盖建筑施工、物业管理、快消拜访等场景，一键部署专属 AI 管家，团队效率提升 90%。',
    tags: ['2个AI管家权限', '20+行业模版', '90+ MCP能力', '一键部署']
  }
] as const

const ENTERPRISE_AI_UPGRADE_VALUE_ITEMS = [
  {
    name: 'AI MCP 数据通道（年）',
    marketPrice: '市场价 ¥8,000+'
  },
  {
    name: '1000万 AI Token',
    marketPrice: '市场价 ¥3,000+'
  },
  {
    name: '蓝领 AI Agent 平台（2个管家）',
    marketPrice: '市场价 ¥50,000+'
  }
] as const

const ENTERPRISE_AI_UPGRADE_CONTACT_BENEFITS = [
  {
    icon: 'file-text',
    title: '免费 AI 数字化方案',
    description: '根据企业场景定制专属落地路径'
  },
  {
    icon: 'tags',
    title: '详细权益与专属报价',
    description: '多种套餐灵活选择，支持对公转账'
  },
  {
    icon: 'customer-service',
    title: '7×12h 在线响应',
    description: '售前咨询到售后服务，全程无忧'
  }
] as const

const ENTERPRISE_AI_UPGRADE_TRUST_ITEMS = [
  {
    icon: 'bank',
    text: '已服务 500+ 企业'
  },
  {
    icon: 'star',
    text: '满意度 98%'
  },
  {
    icon: 'clock-circle',
    text: '平均 5 分钟响应'
  }
] as const

const ENTERPRISE_AI_UPGRADE_FAQS = [
  {
    question: 'Q: 我还不是企业级会员，可以购买升级包吗？',
    answer:
      '升级包为企业级会员专属权益。您可以先升级为企业级会员，再购买升级包，享受组合优惠。我们也提供一站式升级方案，欢迎咨询客服。'
  },
  {
    question: 'Q: 1000万 Token 用完了怎么办？',
    answer:
      '续费即赠新一期 1000 万 Token。如果当期用量超出，可按量补充，享受企业级会员专属折扣价。'
  },
  {
    question: 'Q: 蓝领 AI Agent 平台的 2 个管家权限怎么用？',
    answer:
      '开通后可登录「今日水印相机 × 蓝领 Agent 平台」，从 20+ 行业模版中选择适合您的 AI 管家，一键部署到团队。每个管家涵盖考勤监控、施工记录、巡检管理、报告生成等核心能力，支持自定义配置，团队效率平均提升 90%。'
  }
] as const

const EnterpriseAiUpgradeModal: React.FC<EnterpriseAiUpgradeModalProps> = ({
  visible,
  onCancel
}) => {
  const [activeFaq, setActiveFaq] = useState<number | null>(0)

  const toggleFaq = (index: number) => {
    setActiveFaq((current) => (current === index ? null : index))
  }

  return (
    <Modal
      open={visible}
      footer={null}
      closable={false}
      mask={{ closable: true }}
      destroyOnHidden={true}
      width={860}
      onCancel={onCancel}
      wrapClassName="enterprise-ai-upgrade-modal-wrap"
    >
      <div className="enterprise-ai-upgrade-modal">
        <button
          type="button"
          className="enterprise-ai-upgrade-modal__close"
          onClick={onCancel}
          aria-label="关闭企业AI升级包弹窗"
        >
          ×
        </button>

        <div className="enterprise-ai-upgrade-modal__header">
          <div className="enterprise-ai-upgrade-modal__badge">
            企业级会员专享
          </div>
          <h2>企业智能化升级包</h2>
          <p className="enterprise-ai-upgrade-modal__subtitle">
            开通即锁 3 大专属特权，一站式帮企业降本增效，零门槛落地 AI 数字化。
          </p>
          <div className="enterprise-ai-upgrade-modal__price">
            <span className="enterprise-ai-upgrade-modal__price-original">
              原价 ¥2999
            </span>
            <span className="enterprise-ai-upgrade-modal__price-current">
              ¥1999
            </span>
            <span className="enterprise-ai-upgrade-modal__price-unit">/年</span>
            <span className="enterprise-ai-upgrade-modal__price-discount">
              省 ¥1000
            </span>
          </div>
        </div>

        <div className="enterprise-ai-upgrade-modal__privileges">
          {ENTERPRISE_AI_UPGRADE_PRIVILEGES.map((item) => (
            <div
              key={item.title}
              className="enterprise-ai-upgrade-modal__privilege-card"
            >
              <div className="enterprise-ai-upgrade-modal__privilege-number">
                {item.number}
              </div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              <div className="enterprise-ai-upgrade-modal__privilege-tags">
                {item.tags.map((tag) => (
                  <span key={tag}>{tag}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="enterprise-ai-upgrade-modal__value">
          <div className="enterprise-ai-upgrade-modal__value-header">
            <span className="enterprise-ai-upgrade-modal__value-header-icon">
              HOT
            </span>
            <span>升级包价值对比</span>
            <span className="enterprise-ai-upgrade-modal__value-header-tag">
              超值
            </span>
          </div>
          <div className="enterprise-ai-upgrade-modal__value-items">
            {ENTERPRISE_AI_UPGRADE_VALUE_ITEMS.map((item) => (
              <div
                key={item.name}
                className="enterprise-ai-upgrade-modal__value-item"
              >
                <span className="enterprise-ai-upgrade-modal__value-name">
                  <span className="enterprise-ai-upgrade-modal__value-check">
                    ✓
                  </span>
                  {item.name}
                </span>
                <span className="enterprise-ai-upgrade-modal__value-market">
                  <s>{item.marketPrice}</s>
                </span>
              </div>
            ))}
          </div>
          <div className="enterprise-ai-upgrade-modal__value-total">
            <div className="enterprise-ai-upgrade-modal__value-total-left">
              <span className="enterprise-ai-upgrade-modal__value-total-label">
                总价值
              </span>
              <span className="enterprise-ai-upgrade-modal__value-total-num">
                ¥61,000+
              </span>
            </div>
            <div className="enterprise-ai-upgrade-modal__value-total-arrow">
              →
            </div>
            <div className="enterprise-ai-upgrade-modal__value-total-right">
              <span className="enterprise-ai-upgrade-modal__value-total-original">
                ¥2999
              </span>
              <span className="enterprise-ai-upgrade-modal__value-total-current">
                ¥1999
              </span>
              <span className="enterprise-ai-upgrade-modal__value-total-unit">
                /年
              </span>
            </div>
            <div className="enterprise-ai-upgrade-modal__value-save">
              省 97%
            </div>
          </div>
        </div>

        <div className="enterprise-ai-upgrade-modal__contact">
          <div className="enterprise-ai-upgrade-modal__contact-title">
            <div className="enterprise-ai-upgrade-modal__contact-divider" />
            <span>立即咨询，开启企业 AI 之旅</span>
            <div className="enterprise-ai-upgrade-modal__contact-divider" />
          </div>

          <div className="enterprise-ai-upgrade-modal__contact-body">
            <div className="enterprise-ai-upgrade-modal__contact-card">
              <div className="enterprise-ai-upgrade-modal__contact-qr">
                <img src={wanganqiangQrCode} alt="企业级会员专属客服二维码" />
              </div>
              <div className="enterprise-ai-upgrade-modal__contact-label">
                企业微信扫码咨询
              </div>
              <div className="enterprise-ai-upgrade-modal__contact-name">
                王安强 · 今日水印相机
              </div>
            </div>

            <div className="enterprise-ai-upgrade-modal__contact-info">
              <h4>企业级会员专属客服</h4>
              <p className="enterprise-ai-upgrade-modal__contact-desc">
                一对一为您解答升级包权益，量身定制企业 AI 方案
              </p>
              <div className="enterprise-ai-upgrade-modal__contact-benefits">
                {ENTERPRISE_AI_UPGRADE_CONTACT_BENEFITS.map((item) => (
                  <div
                    key={item.title}
                    className="enterprise-ai-upgrade-modal__contact-benefit"
                  >
                    <div className="enterprise-ai-upgrade-modal__contact-benefit-icon">
                      <BenefitIcon name={item.icon} />
                    </div>
                    <div className="enterprise-ai-upgrade-modal__contact-benefit-copy">
                      <strong>{item.title}</strong>
                      <span>{item.description}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="enterprise-ai-upgrade-modal__contact-trust">
                {ENTERPRISE_AI_UPGRADE_TRUST_ITEMS.map((item) => (
                  <span key={item.text}>
                    <BenefitIcon name={item.icon} />
                    {item.text}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="enterprise-ai-upgrade-modal__faq">
          <h4>常见问题</h4>
          {ENTERPRISE_AI_UPGRADE_FAQS.map((item, index) => {
            const isOpen = activeFaq === index

            return (
              <div
                key={item.question}
                className={`enterprise-ai-upgrade-modal__faq-item ${
                  isOpen ? 'is-open' : ''
                }`}
              >
                <button
                  type="button"
                  className="enterprise-ai-upgrade-modal__faq-question"
                  onClick={() => toggleFaq(index)}
                >
                  <span>{item.question}</span>
                  <span className="enterprise-ai-upgrade-modal__faq-toggle">
                    {isOpen ? '−' : '+'}
                  </span>
                </button>
                <div
                  className={`enterprise-ai-upgrade-modal__faq-answer ${
                    isOpen ? 'is-open' : ''
                  }`}
                >
                  {item.answer}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </Modal>
  )
}

export default EnterpriseAiUpgradeModal
